<template>
  <div
    class="map-center noselect"
    dir="ltr"
    v-resize="onResize"
  >
    <span
      class="toggle-button"
      @click="hidden = !hidden"
    >
      <v-icon small>{{ hidden ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    </span>

    <v-btn
      small
      flat
      color="primary"
      @click="changeCoordinationsSystem"
      :class="{ 'hidden': hidden }"
    >{{ activeSystemTitle }}</v-btn>
    <span
      class="coordinates-span select"
      dir="ltr"
      :class="{ 'hidden': hidden }"
    >{{ coordinates }}</span>
    <v-btn
      icon
      small
      color="primary"
      class="mr-0"
      flat
      :class="{ 'hidden': hidden }"
      @click="copyToClipboard"
    >
      <v-icon small>mdi-content-copy</v-icon>
    </v-btn>
    <v-btn
      icon
      small
      color="primary"
      class="mr-0"
      flat
      :class="{ 'hidden': hidden }"
      @click="openGoogleMaps"
    >
      <v-icon small>mdi-google-maps</v-icon>
    </v-btn>
    <v-btn
      icon
      small
      color="primary"
      flat
      :class="{ 'hidden': hidden }"
      @click="openWeatherMap"
    >
      <v-icon small>mdi-weather-pouring</v-icon>
    </v-btn>
  </div>
</template>

<script>
import proj4 from "proj4";

export default {
  data() {
    return {
      hidden: true,
      activeCoordinatesSystem: 1,
      coordinatesSystems: [
        {
          name: "GEO DDD",
          displayFunction: data => {
            return `${data[1].toFixed(8)} , ${data[0].toFixed(8)}`;
          },
          proj4: "+proj=longlat +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +no_defs"
        },
        {
          name: "GEO DMS",
          displayFunction: data => {
            return this.convertDMS({ lat: data[1], lng: data[0] });
          },
          proj4: "+proj=longlat +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +no_defs"
        },
        {
          name: "UTM",
          zone: true,
          displayFunction: data => {
            return `${this.zone} ${data[0].toFixed(0)} , ${data[1].toFixed(0)}`;
          },
          proj4: "+proj=utm +zone=<ZONE> +ellps=GRS80 +units=m +no_defs"
        }
      ]
    };
  },

  computed: {
    coordinates() {
      let to = Object.assign(
        {},
        this.coordinatesSystems[this.activeCoordinatesSystem]
      );

      if (to.zone) {
        to.proj4 = to.proj4.replace("<ZONE>", this.zone);
      }

      try {
        let result = proj4(to.proj4, [this.lng, this.lat]);
        return to.displayFunction(result);
      } catch (err) {
        console.log(err);
        return "Error";
      }
    },
    zone() {
      return this.utmZoneForLatLng({ lat: this.lat, lng: this.lng });
    },
    activeSystemTitle() {
      let coordinatesSystem = this.coordinatesSystems[
        this.activeCoordinatesSystem
      ];
      return coordinatesSystem.name;
    }
  },

  methods: {
    openGoogleMaps() {
      var strWindowFeatures =
        "location=yes,height=500,width=800,status=yes";
      // q= pin location, ll= map view center, z= zoom level
      const lat = this.lat;
      const lng = this.lng;
      const zoom = this.zoom;
      const pin =
        this.markers && this.markers.length > 0 &&
        this.markers[0].geometry && this.markers[0].geometry.coordinates
          ? `${this.markers[0].geometry.coordinates[1]},${this.markers[0].geometry.coordinates[0]}`
          : `${lat},${lng}`;
      const URL = `https://www.google.com/maps?q=${pin}&ll=${lat},${lng}&z=${zoom}`;
      window.open(URL, "_blank", strWindowFeatures);
    },
    openWeatherMap() {
      var strWindowFeatures =
        "location=yes,height=500,width=800,status=yes";
      var URL =
        `https://www.windy.com/?${this.lat},${this.lng},${this.zoom}`;
      var win = window.open(URL, "_blank", strWindowFeatures);
    },
    copyToClipboard() {
      this.$copyText(this.coordinates).then(() => {
        this.onCopySuccess();
      });
    },
    onCopySuccess() {
      this.$store.dispatch("SHOW_SNACKBAR", {
        text: "הקואורדינטות הועתקו",
        color: "success"
      });
    },
    onCopyError(err) {
      this.$store.dispatch("SHOW_SNACKBAR", {
        text: err.message,
        color: "error"
      });
    },
    onResize(e) {
      // console.log(e);
    },
    changeCoordinationsSystem() {
      this.activeCoordinatesSystem =
        (this.activeCoordinatesSystem + 1) % this.coordinatesSystems.length;
    },
    utmZoneForLatLng({ lat, lng }) {
      let zoneNumber = Math.floor((lng + 180) / 6) + 1;

      if (lat >= 56.0 && lat < 64.0 && lng >= 3.0 && lng < 12.0) {
        zoneNumber = 32;
      }

      // Special zones for Svalbard
      if (lat >= 72.0 && lat < 84.0) {
        if (lng >= 0.0 && lng < 9.0) {
          zoneNumber = 31;
        } else if (lng >= 9.0 && lng < 21.0) {
          zoneNumber = 33;
        } else if (lng >= 21.0 && lng < 33.0) {
          zoneNumber = 35;
        } else if (lng >= 33.0 && lng < 42.0) {
          zoneNumber = 37;
        }
      }
      return zoneNumber;
    },
    toDegreesMinutesAndSeconds(coordinate) {
      var absolute = Math.abs(coordinate);
      var degrees = Math.floor(absolute);
      var minutesNotTruncated = (absolute - degrees) * 60;
      var minutes = Math.floor(minutesNotTruncated);
      var seconds = Math.floor((minutesNotTruncated - minutes) * 60);

      return degrees + "° " + minutes + "’ " + seconds + '"';
    },
    convertDMS({ lat, lng }) {
      var latitude = this.toDegreesMinutesAndSeconds(lat);
      var latitudeCardinal = lat >= 0 ? "N" : "S";

      var longitude = this.toDegreesMinutesAndSeconds(lng);
      var longitudeCardinal = lng >= 0 ? "E" : "W";

      return (
        latitude +
        " " +
        latitudeCardinal +
        " , " +
        longitude +
        " " +
        longitudeCardinal
      );
    }
  },

  props: {
    lat: { type: Number, required: true },
    lng: { type: Number, required: true },
    zoom: { type: Number, default: 10 },
    markers: { type: Array, default: () => [] }
  },

  watch: {},

  mounted() {
    if(this.$vuetify.breakpoint.lgAndUp) {
      this.hidden = false;
    }
  },

  components: {}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.map-center {
  position: absolute;
  left: 50%;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
  bottom: 0;
  z-index: 7;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 0;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-top: 2px solid rgba(0, 0, 0, 0.1);
  border-left: 2px solid rgba(0, 0, 0, 0.1);
  border-right: 2px solid rgba(0, 0, 0, 0.1);

  text-align: center;
}

.coordinates-span {
  background: white;
  padding: 2px 10px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 5px;
  line-height: 28px;
  display: inline-block;
}

.hidden {
  display: none;
}

.toggle-button {
  position: absolute;
  top: 0;
  left: 50%;
  -webkit-transform: translateX(-50%) translateY(-100%);
  transform: translateX(-50%) translateY(-100%);
  padding: 0 5px;
  background-color: rgba(255, 255, 255, 0.9);
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-top: 2px solid rgba(0, 0, 0, 0.1);
  border-left: 2px solid rgba(0, 0, 0, 0.1);
  border-right: 2px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.select {
  -webkit-touch-callout: text; /* iOS Safari */
  -webkit-user-select: text; /* Safari */
  -khtml-user-select: text; /* Konqueror HTML */
  -moz-user-select: text; /* Firefox */
  -ms-user-select: text; /* Internet Explorer/Edge */
  user-select: text; /* Non-prefixed version, currently
                                  supported by Chrome and Opera */
}
</style>


// WEBPACK FOOTER //
// src/components/map/MapCenter.vue